/** Shared PII scrubbers used by every Sentry runtime. Keep this module edge-safe. */

const SENSITIVE_KEY_PATTERNS = [/^card_/i, /^stripe_/i, /^amount_/i];
const PII_KEY_PATTERNS = [
  /^email$/i, /^phone$/i, /^name$/i, /^first_name$/i, /^last_name$/i,
  /^customer_name$/i, /^contact_name$/i, /^address$/i, /^street$/i,
];

const ADDRESS_SUFFIX = '(?:street|st|avenue|ave|road|rd|boulevard|blvd|drive|dr|lane|ln|court|ct|circle|cir|highway|hwy|parkway|pkwy|place|pl|terrace|ter)';
const ADDRESS_PATTERN = new RegExp(`\\b\\d{1,6}\\s+[\\p{L}\\d.'-]+(?:\\s+[\\p{L}\\d.'-]+){0,5}\\s+${ADDRESS_SUFFIX}\\.?\\b`, 'giu');

export function scrubPII(text: string): string {
  return text
    .replace(/\b[\w.+-]+@[\w.-]+\.\w{2,}\b/g, '[EMAIL]')
    .replace(/(?:\+?1[-.\s]?)?\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/g, '[PHONE]')
    .replace(ADDRESS_PATTERN, '[ADDRESS]')
    .replace(/\b\d{5}(?:-\d{4})?\b/g, '[ZIP]');
}

function scrubStructuredValue(value: unknown): unknown {
  if (typeof value === 'string') return scrubPII(value);
  if (Array.isArray(value)) return value.map(scrubStructuredValue);
  if (value === null || typeof value !== 'object') return value;

  const result: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key))) {
      result[key] = '[REDACTED]';
    } else if (PII_KEY_PATTERNS.some((pattern) => pattern.test(key))) {
      result[key] = '[PII_REDACTED]';
    } else {
      result[key] = scrubStructuredValue(child);
    }
  }
  return result;
}

/** Sentry beforeSend callback shared by Node, edge and browser runtimes. */
export function scrubSentryEvent<T>(input: T): T {
  if (!input || typeof input !== 'object') return input;
  const event = input as Record<string, unknown>;

  if (typeof event.message === 'string') event.message = scrubPII(event.message);
  const exception = event.exception as { values?: Array<{ value?: string }> } | undefined;
  exception?.values?.forEach((item) => {
    if (typeof item.value === 'string') item.value = scrubPII(item.value);
  });

  const request = event.request as { data?: unknown; headers?: Record<string, unknown> } | undefined;
  if (request?.data) request.data = '[REDACTED]';
  if (request?.headers) {
    for (const key of Object.keys(request.headers)) {
      if (key.toLowerCase() === 'cookie' || key.toLowerCase() === 'authorization') {
        delete request.headers[key];
      }
    }
  }

  for (const key of ['extra', 'contexts', 'tags', 'user', 'breadcrumbs']) {
    if (event[key] !== undefined) event[key] = scrubStructuredValue(event[key]);
  }
  return input;
}

/** Sentry beforeBreadcrumb callback, also safe to use from the browser bundle. */
export function scrubSentryBreadcrumb<T>(input: T): T {
  if (!input || typeof input !== 'object') return input;
  return scrubStructuredValue(input) as T;
}
